"use client";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/useToast";
import { ChatCompletionService } from "@/services/openai/chatCompletionService";
import { Loader2, SendIcon, Sparkles } from "lucide-react";
import PropTypes from "prop-types";
import { useState } from "react";

export const GENERATOR_PRESETS = {
  system: {
    title: "Generate System Message",
    description: "Generate a system message for your chat model",
    systemPrompt: `You are an expert at crafting effective system messages for large language models.
Given a description of desired model behavior, you generate clear, concise system messages that:
- Establish the model's role and core capabilities
- Define key behavioral guidelines
- Set clear boundaries and limitations
- Specify the expected interaction style
- Include relevant domain expertise

Your output should be a single system message without any additional explanation or formatting.`,
    userPrompt: `Generate an OpenAI system message for the following description: `,
  },
  instructions: {
    title: "Generate Assistant Instructions",
    description: "Generate instructions for your AI assistant",
    systemPrompt: `You are an expert at creating instructions for AI assistants.
Given a description of the assistant's purpose, you generate comprehensive instructions that:
- Define the assistant's role and expertise
- Outline main capabilities and limitations
- Specify interaction style and tone
- Include domain-specific knowledge requirements
- Set guidelines for tool usage with specific tool references

REQUIREMENTS:
- Format your entire response in XML
- Use proper XML tags to organize sections
- Start with an <instructions> root element for the core assistant definition
- Use <role> for defining the assistant's role
- Use <capabilities> with nested <capability> items
- Use <guidelines> with nested <guideline> items
- Use <examples> with nested <example> tags
- Use <emphasis> for key points
- The instructions should begin with a <role> that states "You are a ... assistant"
- Include 2-3 <example> responses to different types of user requests
- For each example that uses tools, include the specific tools that would be used with <tool> tags
- Format tool references like: <tool>search for recent information using web_search</tool> or <tool>generate an image with image_gen</tool>
- Specifically reference the available tools in your guidelines when appropriate

ADDITIONAL SECTIONS:
IMPORTANT: ALWAYS include these additional sections after the main <instructions> element whenever there is ANY mention or implication of:
* company/business context
* industry details
* specific tasks
* process steps
* research methodologies
* output requirements
* response formats
* or any other structured workflow elements

1. <context> - REQUIRED for ANY domain-specific background information
   - Include relevant sub-elements like <business_name>, <industry>, <current_focus>, etc.
   - This section should contain detailed context the assistant needs to operate effectively
   - If the user mentions any company, industry, or domain, you MUST include this section

2. <task> - REQUIRED for ANY specific workflow steps or methodologies
   - Should contain a numbered or bulleted list of specific steps or processes
   - Must be included if the user mentions any specific tasks, steps, or process requirements
   - Include all tasks mentioned or implied by the user's request

3. <approach> - REQUIRED for ANY specific approach to problem-solving
   - Describes research depth, reasoning style, or strategic methods
   - Must be included if the user indicates how thoroughly research should be conducted
   - Always include if the assistant needs to follow a specific methodology

4. <output_format> - REQUIRED for ANY specific response structure requirements
   - Define exactly how responses should be formatted
   - Must be included if the user indicates any preference for how results should be presented
   - Always include if structured outputs (like JSON, XML, or specific formats) are needed

FORMATTING GUIDELINES FOR READABILITY:
- Each major XML section should be clearly separated with line breaks
- Start each new primary tag (<role>, <capabilities>, <guidelines>, etc.) on a new line
- Each capability, guideline, and example should be on separate lines with proper indentation
- Use consistent indentation for nested elements to enhance readability
- Include empty lines between major sections for better visual separation
- Format should be visually structured and easy to read
- Emphasis tags should stand out clearly from surrounding text

IMPORTANT GUIDELINES:
- **Current Data Requirement**: Emphasize that web_search MUST be used for ANY user request requiring current information or real-time data
- **Parallel Tool Usage**: When a user requests multiple tool-related tasks, emphasize that the tools should be called in parallel rather than sequentially when possible

Your output should be clear instructions in XML format without any additional explanation or preamble.`,
    userPrompt: `Generate an OpenAI assistant instructions for the following description: `,
  },
  function: {
    title: "Generate Function Schema",
    description: "Generate an OpenAI function call schema",
    systemPrompt: `You are an expert at creating function schemas for AI model function calling.
Given a description of the desired function, you generate a valid JSON schema that strictly follows OpenAI function calling specifications.

Your output MUST:
1. Include a clear, descriptive "name" in snake_case format
2. Include a detailed "description" explaining the function's purpose
3. Have "parameters" with "type": "object"
4. Set "strict": true to enforce strict parameter validation
5. Include proper parameter definitions with:
   - Clear descriptions
   - Appropriate types
   - Required fields listed
   - Parameter constraints where applicable
   - Enum values when parameters have fixed options

Example format:
{
  "name": "example_function",
  "description": "A clear description of what this function does",
  "parameters": {
    "type": "object",
    "strict": true,
    "properties": {
      "param1": {
        "type": "string",
        "description": "Clear description of parameter 1",
        "enum": ["option1", "option2"]
      }
    },
    "required": ["param1"]
  }
}

Return ONLY the valid JSON schema without any additional explanation or formatting.`,
    userPrompt: `Generate a complete OpenAI function schema for the following functionality: `,
  },
};

export function InstructionsGeneratorPopup({
  type = "instructions",
  onGenerated,
  isLoading,
  setIsLoading,
  className,
  toolNames = [],
}) {
  const [open, setOpen] = useState(false);
  const [taskDescription, setTaskDescription] = useState("");
  const { toast } = useToast();

  const getConfigData = (field) => {
    if (type === "system") {
      return GENERATOR_PRESETS.system[field];
    }
    if (type === "instructions") {
      return GENERATOR_PRESETS.instructions[field];
    }
    if (type === "function") {
      return GENERATOR_PRESETS.function[field];
    }
  };

  const generateInstructions = async () => {
    if (!taskDescription.trim()) {
      toast({
        title: "Error",
        description: "Please provide a task description",
        variant: "destructive",
      });
      return;
    }
    const chatCompletionService = new ChatCompletionService();

    setIsLoading(true);
    try {
      // Set system prompt for generating instructions
      chatCompletionService.setSystemPrompt(getConfigData("systemPrompt"));

      // Prepare user message with tool names if available
      let userMessage = `${getConfigData("userPrompt")} ${taskDescription}`;

      // Add tool information if this is an instructions generator and tools are provided
      if (type === "instructions" && toolNames && toolNames.length > 0) {
        userMessage += `\n\nThe assistant has access to the following tools. Make sure to reference them specifically in your instructions and examples:\n${toolNames.join(", ")}`;
      }

      const response = await chatCompletionService.createChatCompletion(
        [
          {
            role: "user",
            content: userMessage,
          },
        ],
        {
          featureId: "assistant_generation",
        }
      );

      const generatedInstructions = response.choices[0].message.content;
      onGenerated(generatedInstructions);
      setOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to generate ${getConfigData("title")}: ${
          error.message
        }`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="inline-block" style={{ position: "relative", zIndex: 100 }}>
      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`flex items-center gap-2 ${className || ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <Sparkles className="h-3 w-3" />
            {/* <span>Generate</span> */}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-60 p-0 border border-purple-500/30 rounded-md shadow-lg bg-transparent backdrop-blur-sm"
          align="end"
          side="right"
          alignOffset={-5}
          sideOffset={10}
          style={{ zIndex: 9999 }}
          forceMount
          avoidCollisions={true}
        >
          <div className="p-2 rounded-md relative">
            <Textarea
              placeholder="Describe the task or purpose of the AI assistant..."
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              className="min-h-[120px] w-full border-purple-500/30 focus:border-none focus-visible:outline-none focus-visible:ring-0 bg-white/10 dark:bg-zinc-800/10 resize-none text-sm focus:shadow-[0_0_0_2px_rgba(88,28,135,0.8),0_0_0_4px_rgba(107,33,168,0.5)] focus:bg-gradient-to-br focus:from-purple-700/10 focus:via-purple-600/8 focus:to-purple-500/6 transition-all duration-200"
              disabled={isLoading}
              style={{
                "--tw-ring-color": "transparent",
              }}
            />
            <div className="absolute bottom-4 right-4 pointer-events-none">
              <Button
                size="icon"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  generateInstructions();
                }}
                disabled={isLoading}
                className="h-8 w-8 rounded-md bg-transparent border-purple-500 text-purple-500 hover:bg-purple-500/10 hover:text-purple-600 shadow-sm pointer-events-auto"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SendIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

InstructionsGeneratorPopup.propTypes = {
  type: PropTypes.string,
  onGenerated: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  className: PropTypes.string,
  toolNames: PropTypes.array,
};

export default InstructionsGeneratorPopup;
